import { useEffect } from "react";
import { useWorkspace } from "./workspace-context";
import { TabParamsProvider } from "./tab-params-context";
import { resolveRouteComponent } from "./route-manifest";
import { NewWorkspaceTabPage } from "@/routes/workspaces.new";
import { cn } from "@/lib/utils";

const EMPTY_PARAMS: Record<string, string> = {};

interface WorkspaceContentProps {
  className?: string;
}

function TabContent({
  tab,
}: {
  tab: { id: string; route: string; params?: Record<string, string> };
}) {
  const RouteComponent = resolveRouteComponent(tab.route);

  useEffect(() => {
    if (!RouteComponent) {
      console.warn("Unknown workspace route:", tab.route);
    }
  }, [RouteComponent, tab.route]);

  return (
    <TabParamsProvider params={tab.params ?? EMPTY_PARAMS}>
      <div className="min-h-full p-6 lg:p-8">
        {RouteComponent ? (
          <RouteComponent />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <p>Unknown route: {tab.route}</p>
            <p className="text-sm">This tab could not be loaded.</p>
          </div>
        )}
      </div>
    </TabParamsProvider>
  );
}

export function WorkspaceContent({ className }: WorkspaceContentProps) {
  const { tabs, activeTabId } = useWorkspace();

  if (tabs.length === 0) {
    return (
      <TabParamsProvider params={{}}>
        <div className="h-full overflow-auto p-6 lg:p-8">
          <NewWorkspaceTabPage />
        </div>
      </TabParamsProvider>
    );
  }

  return (
    <div className={cn("relative h-full overflow-hidden", className)}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;

        return (
          <div
            key={tab.id}
            className={cn(
              "absolute inset-0 overflow-y-auto",
              isActive ? "visible opacity-100" : "invisible opacity-0"
            )}
            style={{
              pointerEvents: isActive ? "auto" : "none",
            }}
          >
            <TabContent tab={tab} />
          </div>
        );
      })}
    </div>
  );
}
